
import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Facebook } from "lucide-react";
import { useMetaConnect } from "./hooks/useMetaConnect";
import { ConnectionStatusAlert } from "./ConnectionStatusAlert";
import { FacebookLoginTab } from "./FacebookLoginTab";
import { OAuthLoginTab } from "./OAuthLoginTab";
import { ConnectionMethod, MetaConnectDialogProps, ConnectionState } from "./types";

export const MetaConnectDialog: React.FC<MetaConnectDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const {
    activeTab,
    setActiveTab,
    connectionStatus,
    setConnectionStatus,
    isInitialized,
    isLoading,
    sdkError,
    sdkConfigError,
    handleConnect
  } = useMetaConnect(onSuccess);
  
  // Handle SDK configuration errors
  useEffect(() => {
    if (sdkConfigError) {
      setConnectionStatus({
        state: ConnectionState.WARNING,
        message: "Es gibt ein Problem mit der Facebook SDK-Konfiguration. Bitte aktiviere 'Login mit JavaScript SDK' in deinem Facebook Developer Account."
      });
    } else if (sdkError) {
      setConnectionStatus({
        state: ConnectionState.ERROR,
        message: sdkError
      });
    }
  }, [sdkError, sdkConfigError, setConnectionStatus]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center">
            <Facebook size={24} className="text-[#1877F2] mr-2" />
            <DialogTitle>Mit Meta verbinden</DialogTitle>
          </div>
          <DialogDescription>
            Verbinde dein Meta-Konto, um Werbekampagnen zu analysieren
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue={ConnectionMethod.FACEBOOK} value={activeTab} onValueChange={(value) => setActiveTab(value as ConnectionMethod)}>
          <TabsList className="grid grid-cols-2 w-full mb-4">
            <TabsTrigger value={ConnectionMethod.FACEBOOK}>Facebook Login</TabsTrigger>
            <TabsTrigger value={ConnectionMethod.OAUTH}>OAuth</TabsTrigger>
          </TabsList>
          
          <TabsContent value={ConnectionMethod.FACEBOOK} className="space-y-4">
            <FacebookLoginTab 
              isLoading={isLoading}
              isInitialized={isInitialized}
              connectionStatus={connectionStatus}
              handleConnect={handleConnect}
            />
          </TabsContent>
          
          <TabsContent value={ConnectionMethod.OAUTH} className="space-y-4">
            <OAuthLoginTab
              connectionStatus={connectionStatus}
              handleConnect={handleConnect}
            />
          </TabsContent>
        </Tabs>
        
        <div>
          <ConnectionStatusAlert connectionStatus={connectionStatus} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
